import { PropType } from 'vue';
import { mapState } from 'vuex';
import UserItem from './UserItem/UserItem.vue';
import { AccountInfo } from '@azure/msal-browser';
import plantsClient from '@/core/services/plants';
import { Vue, Options } from 'vue-class-component';
import {
  PlantDetailDTO,
  PlantSharingDTO,
  TransferOwnershipDTO,
} from '@/../client-generator/generated-clients/plants';
import { getUserInitialsForBubble } from '@/core/utils/utils';
import ConfirmDeleteUser from './ConfirmDeleteUser/ConfirmDeleteUser.vue';
import DialogConfirmTransferOwnership from '@/views/components/DialogConfirmTransferOwnership/DialogConfirmTransferOwnership.vue';

@Options({
  name: 'MenuSharing',
  props: {
    plant: {
      type: Object as PropType<PlantDetailDTO>,
      required: true,
    },
  },
  components: {
    UserItem,
    ConfirmDeleteUser,
    DialogConfirmTransferOwnership,
  },
  emits: ['add-invited-user', 'remove-sharing-user'],
  computed: {
    ...mapState(['account']),
  },
})
export default class MenuSharing extends Vue {
  plant!: PlantDetailDTO;
  getUserInitials = getUserInitialsForBubble;

  isLoading = false;
  isPopperVisible = false;
  isConfirmDeleteUserVisible = false;
  isVisibleTransferOwnershipConfirm = false;

  doShow = false;
  newUserToShare: string | null = null;

  tempUser: PlantSharingDTO | null = null;
  account!: AccountInfo;

  get isMeTheOwner() {
    return this.plant.owner?.id === this.account?.localAccountId;
  }

  get plantName() {
    if (this.plant) {
      return this.plant.name;
    }

    return '';
  }

  togglePopper() {
    this.isPopperVisible = !this.isPopperVisible;
  }

  async invitePerson() {
    this.isLoading = true;

    try {
      await plantsClient.sharingPOST(
        this.plant.desktopId ?? -1,
        this.newUserToShare ?? ''
      );

      this.$emit('add-invited-user', this.newUserToShare);
      this.newUserToShare = '';
    } catch (error) {
      console.log(error);
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.isLoading = false;
    }
  }

  async handleDeleteUser(user: PlantSharingDTO) {
    this.toggleConfirmDeleteUser();
    this.tempUser = user;
  }

  async handleTransferOwnership(user: PlantSharingDTO) {
    this.toggleTransferOwnershipConfirm();
    this.tempUser = user;
  }

  toggleConfirmDeleteUser() {
    this.isConfirmDeleteUserVisible = !this.isConfirmDeleteUserVisible;
    this.isPopperVisible = !this.isPopperVisible;
  }

  toggleTransferOwnershipConfirm() {
    this.isVisibleTransferOwnershipConfirm = !this
      .isVisibleTransferOwnershipConfirm;
    this.isPopperVisible = !this.isPopperVisible;
  }

  async deleteUser() {
    this.isLoading = true;

    try {
      await plantsClient.sharingDELETE(
        this.plant.desktopId ?? -1,
        this.tempUser?.confirmed
          ? this.tempUser?.email
          : this.tempUser?.invitedEmail
      );
      if (this.tempUser?.userId === this.account.localAccountId) {
        this.$router.push({
          name: 'page-plants-hub',
        });
      } else {
        this.$emit('remove-sharing-user', this.tempUser);
      }
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.isLoading = false;
      this.tempUser = null;
    }
  }

  async transferOwnership(password: string) {
    this.isLoading = true;

    try {
      await plantsClient.transferOwnership(
        this.plant.desktopId ?? -1,
        this.tempUser?.confirmed
          ? this.tempUser?.email
          : this.tempUser?.invitedEmail,
        new TransferOwnershipDTO({
          shouldCurrentUserRightsBeRemoved: false,
          password,
        })
      );
      this.$router.push({ name: 'page-plants-hub' });
    } catch (error) {
      let message = this.$t('general.operationError');
      switch (error.status) {
        case 400:
          message = this.$t('general.invalidPassword');
          break;
      }
      this.$toast.add({
        severity: 'error',
        summary: message,
        closable: true,
        life: 3000,
      });
    } finally {
      this.isLoading = false;
      this.tempUser = null;
    }
  }
}
